/**
 * leakageEstimate — shared, defensible leakage-figure calculator.
 *
 * Input: an array of pillar-style scores (0–100) representing the
 * components of a learning value chain, plus the org's annual
 * value-at-stake (the L&D-attributable share of operating profit).
 *
 * Output: BOTH the compounded-gate leakage figure and the
 * weighted-average leakage figure, framed as a defensible RANGE so
 * stakeholders can read the uncertainty band rather than a single
 * point estimate.
 *
 * Why two models
 * ──────────────
 * The compounded-gate model — `retention = ∏(score/100)` — assumes
 * each pillar acts as an independent serial gate, multiplying the
 * upstream value forward. It's mathematically clean but treats
 * pillar deficits as INDEPENDENT, which they aren't (engagement &
 * outcomes are correlated; capability & operations overlap). With
 * 5 pillars in the 67–80 range this typically lands at ~20%
 * retention → ~80% leakage. Right-side-of-defensible CFO answer:
 * this is the worst-case stress-test ceiling.
 *
 * The weighted-average model — `retention = mean(scores)/100` —
 * assumes pillars contribute LINEARLY to the realized fraction.
 * This is what most published L&D economic studies use (Bersin,
 * ATD, Brinkerhoff). For 5 pillars at 70 the figure is 70% → 30%
 * leakage. Left-side-of-defensible CFO answer: this is the
 * grounded lower bound.
 *
 * Reality lives somewhere in the band. Showing both lets the user
 * own the assumption explicitly instead of inheriting whichever
 * model the dashboard happened to pick. Used by every weak-link
 * callout in the app (LEI / PE / ROE / ROI / Maturity) so the
 * methodology is uniform across engines.
 */

const _clamp01 = (v) => Math.max(0, Math.min(1, v));


/**
 * Compute the dual-anchor leakage estimate.
 *
 * @param {number[]} componentScores 0–100 component scores. Single-
 *   element arrays (engine/maturity composites) collapse the two
 *   models to the same number — the function still returns both,
 *   so the UI methodology tooltip stays uniform across surfaces.
 * @param {number} annualValueAtStakeUsd L&D-attributable share of
 *   operating profit (revenue × margin × 5%). null/undefined
 *   produces a null-shaped result that the UI can short-circuit on.
 * @returns {{
 *   compounded:    { retention: number, leakageUsd: number },
 *   weightedAvg:   { retention: number, leakageUsd: number },
 *   rangeUsd:      { lowUsd: number, highUsd: number },
 *   isCollapsed:   boolean,
 * }|null}
 */
export function computeLeakageEstimate(componentScores, annualValueAtStakeUsd) {
  if (typeof annualValueAtStakeUsd !== 'number' || !isFinite(annualValueAtStakeUsd)) {
    return null;
  }
  const valid = (componentScores || []).filter(
    (s) => typeof s === 'number' && isFinite(s),
  );
  if (valid.length === 0) return null;

  const compoundedRetention = valid.reduce(
    (acc, s) => acc * _clamp01(s / 100),
    1,
  );
  const meanScore = valid.reduce((a, b) => a + b, 0) / valid.length;
  const weightedAvgRetention = _clamp01(meanScore / 100);

  const compoundedLeakage = annualValueAtStakeUsd * (1 - compoundedRetention);
  const weightedLeakage   = annualValueAtStakeUsd * (1 - weightedAvgRetention);

  const lowUsd  = Math.min(weightedLeakage, compoundedLeakage);
  const highUsd = Math.max(weightedLeakage, compoundedLeakage);

  return {
    compounded:  { retention: compoundedRetention, leakageUsd: compoundedLeakage },
    weightedAvg: { retention: weightedAvgRetention, leakageUsd: weightedLeakage },
    rangeUsd:    { lowUsd, highUsd },
    // When there's only one component or the two models happen to
    // converge, suppress the "range" framing in the UI — show a
    // single figure and keep the methodology tooltip for consistency.
    isCollapsed: valid.length <= 1 || Math.abs(highUsd - lowUsd) / Math.max(highUsd, 1) < 0.02,
  };
}


/**
 * Compute the unlocked-on-fix dollar projection using both models.
 * Mirrors `computeLeakageEstimate` for the "after fix" projection
 * so the two halves of the weak-link callout (leaking now vs
 * unlocked on fix) speak the same methodology.
 */
export function computeUnlockEstimate(beforeScores, afterScores, annualValueAtStakeUsd) {
  const before = computeLeakageEstimate(beforeScores, annualValueAtStakeUsd);
  const after  = computeLeakageEstimate(afterScores,  annualValueAtStakeUsd);
  if (!before || !after) return null;

  const compoundedDelta  = after.compounded.retention  - before.compounded.retention;
  const weightedAvgDelta = after.weightedAvg.retention - before.weightedAvg.retention;

  return {
    compounded:  { deltaRetention: compoundedDelta,  unlockedUsd: annualValueAtStakeUsd * compoundedDelta  },
    weightedAvg: { deltaRetention: weightedAvgDelta, unlockedUsd: annualValueAtStakeUsd * weightedAvgDelta },
    rangeUsd:    {
      lowUsd:  Math.min(annualValueAtStakeUsd * compoundedDelta, annualValueAtStakeUsd * weightedAvgDelta),
      highUsd: Math.max(annualValueAtStakeUsd * compoundedDelta, annualValueAtStakeUsd * weightedAvgDelta),
    },
    isCollapsed: before.isCollapsed && after.isCollapsed,
  };
}


/**
 * Format a leakage range for headline display.
 * - Range: "$3.9M – $11.4M /yr"
 * - Collapsed: "$3.9M /yr"
 *
 * Currency formatting matches `formatMoney` in the callout files
 * so labels read identically across surfaces.
 */
export function formatLeakageRange(estimate, formatMoney) {
  if (!estimate) return '';
  if (estimate.isCollapsed) {
    // Use the weighted-average — the defensible lower bound — when
    // the two models converge or only one component is supplied.
    return formatMoney(estimate.weightedAvg.leakageUsd);
  }
  return `${formatMoney(estimate.rangeUsd.lowUsd)} – ${formatMoney(estimate.rangeUsd.highUsd)}`;
}


/**
 * Tooltip body shared by every weak-link callout in the app, so the
 * methodology disclosure is identical wherever a leakage figure
 * appears. Keep this prose stable — talk-track + customer-facing
 * collateral references this exact wording.
 */
export const LEAKAGE_METHODOLOGY_BODY = (
  'Two defensible models are computed and reported as a range:\n\n' +
  '• Lower bound (Weighted-Average) — assumes each component contributes ' +
  'linearly to realized value. Aligned with published L&D economic ' +
  'studies (Bersin, ATD, Brinkerhoff). Use this as the conservative, ' +
  'CFO-grounded estimate.\n\n' +
  '• Upper bound (Compounded-Gate) — assumes each component acts as an ' +
  'independent serial gate that multiplies value forward, ' +
  '`retention = ∏(score/100)`. Mathematically clean but treats pillar ' +
  'deficits as independent (they\'re actually correlated), so it ' +
  'over-states leakage. Use this as the worst-case stress-test ceiling.\n\n' +
  'Both anchors share the same Annual Value at Stake input ' +
  '(revenue × operating margin × 5% L&D-attributable share of operating profit). ' +
  'When only one component is available the two models collapse to the same number.'
);
